<template>
  <FitHalfScreenDialogV2
    class="coupon-info-dialog"
    :title="titleText"
    ref="refCouponDetailDialog"
    @onClose="onClose"
    :isAppendToBody="true">
    <div
      slot="bd"
      :class="['coupon-rule', { en: isLangEn }]"
      v-stat.brow="{
        tag: 'coupon-detail-dialog.comp-brow|组件曝光',
        ext: biExt,
      }">
      <div class="coupon-dialog-info" v-if="couponInfo">
        <div class="sp-info-wrapper" v-if="couponInfo.brandName">
          <img :src="couponInfo.couponIcon" alt="">
          <span class="font-32-medium">{{ couponInfo.brandName }}</span>
        </div>
        <div class="coupon-info-wrapper">
          <BasicCouponV2 :couponInfo="couponInfo" :extraParams="extraParams"></BasicCouponV2>
        </div>
        <div class="rule-info-wrapper">
          <div class="rule-row">
            <div class="rule-left" v-html="tI18n('有效期限')"></div>
            <div class="rule-right">
              {{ validPeriod }}
              <span class="expire-tip" v-if="isShowExpireTip">{{ tI18n('即將到期') }}</span>
            </div>
          </div>
          <div class="rule-row">
            <div class="rule-left" v-html="tI18n('適用商戶')"></div>
            <div class="rule-right">{{ couponInfo.couponRange }}</div>
          </div>
          <div class="rule-row">
            <div class="rule-left" v-html="tI18n('適用條件')"></div>
            <div class="rule-right" role="text" :aria-label="couponCondAriaLabel">
              {{ couponCond }}
            </div>
          </div>
        </div>
        <!-- 先不处理tnc 和使用方式 -->
      </div>

      <div class="dialog-footer" v-if="btnInfo">
        <slot name="upperBtn"></slot>
        <Button
          @click="onClickBtn"
          :size="btnInfo.size"
          :disabled="btnInfo.isDisabled"
          v-if="btnInfo.isShowBtn"
          :type="btnInfo.type"
          v-stat.click="{
            tag: 'coupon-detail-dialog.btn-click|按钮点击',
            ext: biExt,
          }">{{ btnInfo.btnText }}</Button>
        <slot name="underBtn"></slot>
      </div>
    </div>
  </FitHalfScreenDialogV2>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue-demi';
import dayjs from 'dayjs';
import { biClick } from '@tencent/fit-bi-sdk';
import { language, url } from '@tencent/ppd-base-libs';
import { i18n, initLang } from './i18n';
import { ExpireType, QryAndFormatCouponItem, UtilsTypes } from '@tencent/wphk-act-base-types';
import { DetailBtnConfig } from './types';
import { BasicCouponV2 } from '@tencent/wphk-act-base-components';
import { CouponInfo, parseCurrencyAria } from '@tencent/wphk-act-base-ui';
import { FitHalfScreenDialogV2 } from '@tencent/wphk-ui';

import Button from './common/button.vue';

import { getValidPeriod, checkIsSpCoupon } from './business/format-coupon';

export default defineComponent({
  name: 'CouponDetailDialog',
  components: {
    FitHalfScreenDialogV2,
    Button,
    BasicCouponV2,
  },
  props: {
    /** 语言 */
    lang: String as PropType<UtilsTypes.LanguageTypeEnum>,
    /** 券数据，同 BasicCouponV2, 目前只支持新数据源couponInfo */
    couponInfo: Object as PropType<QryAndFormatCouponItem>,
    /** 额外券数据，同 BasicCouponV2 */
    extraParams: Object as PropType<CouponInfo>,
    /** 弹层标题 */
    titleText: String,
    /** btn信息 */
    btnInfo: Object as PropType<DetailBtnConfig>,
    /** 当前系统时间，不传则默认new Date() */
    sysTime: String,
  },
  setup(prop, context) {
    initLang(prop.lang);
    // ---- ref句柄 ----
    // 半弹层
    const refCouponDetailDialog = ref<FitHalfScreenDialogV2 | null>(null);

    // ---- data/computed ----
    // 语言
    const isLangEn = language.isEn();
    // 是否为商户展示券
    const isSpCoupon = computed(() => checkIsSpCoupon(prop.couponInfo?.couponType));
    // 是否展示过期提示
    const isShowExpireTip = computed(() => {
      // 是否为固定时间类型
      const isFixedTime = prop.couponInfo?.expireType === ExpireType.FIXED;
      // 当前时间
      const curTime = dayjs(prop.sysTime || new Date());
      const exTime = dayjs(prop.couponInfo?.expireTime);
      const isNear3Day = exTime.diff(curTime, 'day') <= 3 ;
      return isFixedTime && isNear3Day;
    });
    const biExt = computed(() => (
      {
        fcouponName: prop.couponInfo?.couponName,
        fmtn: url.getUrlParam('mtn'), // 物料MTN
        fbatchid: url.getUrlParam('batchid'), // 物料批次Id
        fcampaignid: url.getUrlParam('campaignid'), // 物料计划Id
        fspid: prop.couponInfo?.spid || url.getUrlParam('spId'),  // 商户Id
        fcouponId: prop.couponInfo?.goodsId,  // 券Id
        // @ts-ignore
        fcouponState: prop.couponInfo?.couponState, // 有就上报，没有就不上报
      }
    ));
    // 有效期限
    const validPeriod = computed(() => getValidPeriod(prop.couponInfo));
    // 使用条件
    const couponCond = computed(() => {
      if (!prop.couponInfo) return '';
      const { couponCond, requiredNum } = prop.couponInfo;
      return  isSpCoupon.value ? couponCond : i18n('消費滿HK$x可用', { requiredNum });
    });
    // 无障碍
    const couponCondAriaLabel = computed(() => parseAriaLabel(couponCond.value));

    // ---- 事件 ----
    // 弹层关闭
    function onClose() {
      // @ts-ignore
      biClick('coupon-detail-dialog.comp-close|组件关闭', biExt.value);
      context.emit('onClose');
    };
    // 按钮点击
    function onClickBtn() {
      context.emit('onBtnClick');
    }

    // ---- 辅助方法 ----
    /** 转换为无障碍提示文案（主要是对金额处理） */
    function parseAriaLabel(text: string) {
      const { currencyType = UtilsTypes.CurrencyType.HKD } = prop.couponInfo ?? {};
      return parseCurrencyAria(text, true, currencyType);
    }

    return {
      refCouponDetailDialog,

      isSpCoupon,
      validPeriod,
      isShowExpireTip,
      couponCond,
      couponCondAriaLabel,
      biExt,
      isLangEn,

      /** 按钮点击 */
      onClickBtn,
      /** 半弹层关闭 */
      onClose,

      /** 供调用方使用： 打开券详情弹窗 */
      showDialog() {
        refCouponDetailDialog.value!.showDialog();
      },
      /** 供调用方使用： 关闭券详情弹窗 */
      hideDialog() {
        refCouponDetailDialog.value!.hideDialog();
      },

      tI18n: i18n,
    };
  },
});
</script>

<style lang="less" scoped>
@import url('./less/index.less');
</style>
